import { __rest, __assign } from '../../external/tslib/tslib.es6.mjs.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useIMask } from 'react-imask';
import { MaskedRange, MaskedEnum } from 'imask';
import { useRef, useEffect } from 'react';
import { useOverlayTrigger } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';
import { Input } from '../input/Input.js';
import { ClockIcon } from '../../icons/ClockIcon.js';
import { Popover } from '../../base/popover/Popover.js';
import { useSetNativeValue } from '../../hooks/useSetNativeValue/useSetNativeValue.js';
import styles from './TimeInput.module.scss.js';
import { TimePicker } from './timePicker/TimePicker.js';

var maskOptions = {
    overwrite: true,
    autofix: true,
    lazy: true,
    mask: 'H:M AM',
    blocks: {
        H: {
            mask: MaskedRange,
            placeholderChar: 'H',
            from: 1,
            to: 12,
            maxLength: 2,
        },
        M: {
            mask: MaskedRange,
            placeholderChar: 'M',
            from: 0,
            to: 59,
            maxLength: 2,
        },
        AM: {
            mask: MaskedEnum,
            enum: ['AM', 'PM', 'am', 'pm', 'Am', 'Pm', 'aM', 'pM'],
        },
    },
};
var autoCorrectValue = function (value) {
    var _a = convertTimeToObject(value), hour = _a.hour, minute = _a.minute, isAM = _a.isAM;
    if (typeof hour === 'number' && !isNaN(hour)) {
        return parseTimeObject(hour, minute, isAM);
    }
    return '';
};
var convertTimeToObject = function (value) {
    var splitValue = (value || '').slice(0, 5).split(':');
    var isAM = !(value === null || value === void 0 ? void 0 : value.toUpperCase().includes('P'));
    var hour = parseInt(splitValue[0]);
    var minute = splitValue[1] ? parseInt(splitValue[1]) : 0;
    return {
        hour: hour === 0 ? 1 : hour,
        minute: minute,
        isAM: isAM,
    };
};
var parseTimeObject = function (hour, minute, isAM) {
    return "".concat(hour.toString().padStart(2, '0'), ":").concat(minute.toString().padStart(2, '0'), " ").concat(isAM ? 'AM' : 'PM');
};
var TimeInput = function (_a) {
    var inputProps = _a.inputProps, size = _a.size, props = __rest(_a, ["inputProps", "size"]);
    var state = useOverlayTriggerState({});
    var _b = useIMask(maskOptions), ref = _b.ref, maskRef = _b.maskRef, setValue = _b.setValue;
    var triggerRef = useRef(null);
    var _c = useOverlayTrigger({ type: 'dialog' }, state, triggerRef), _d = _c.triggerProps; _d.onPress; var triggerProps = __rest(_d, ["onPress"]), overlayProps = _c.overlayProps;
    var setNativeValue = useSetNativeValue();
    // Update a value of the iMask when init value provided in the input
    useEffect(function () {
        var _a;
        if (inputProps.value) {
            setValue(inputProps.value);
            (_a = maskRef.current) === null || _a === void 0 ? void 0 : _a.updateValue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Updates only when value is correct
    var onChange = function (e) {
        var _a;
        var correctedValue = autoCorrectValue(e.target.value);
        if (correctedValue === e.target.value) {
            (_a = maskRef.current) === null || _a === void 0 ? void 0 : _a.updateValue();
            inputProps.onChange(e);
        }
    };
    // Check and correct a value when possible (if value not valid then clear the input)
    var onBlur = function (event) {
        var _a, _b, _c;
        var correctedValue = autoCorrectValue((_a = maskRef.current) === null || _a === void 0 ? void 0 : _a.value);
        setValue(correctedValue);
        (_b = maskRef.current) === null || _b === void 0 ? void 0 : _b.updateValue();
        var triggerUpdate = __assign(__assign({}, event), { target: __assign(__assign({}, event.target), { value: correctedValue }) });
        (_c = inputProps.onBlur) === null || _c === void 0 ? void 0 : _c.call(inputProps, event);
        inputProps.onChange(triggerUpdate);
    };
    var onPickTime = function (hour, minute, isAM) {
        var _a;
        var correctedValue = parseTimeObject(hour, minute, isAM);
        var input = ref.current;
        setNativeValue(input, correctedValue);
        (_a = maskRef.current) === null || _a === void 0 ? void 0 : _a.updateValue();
        input.dispatchEvent(new Event('change', { bubbles: true }));
    };
    var formattedValue = convertTimeToObject(inputProps.value);
    return (jsxs(Fragment, { children: [jsx(Input, __assign({ placeholder: "HH:MM AM", maxLength: 8, ref: ref }, props, { size: size, inputProps: __assign(__assign({}, inputProps), { value: undefined, onBlur: onBlur, onChange: onChange }), rightElement: jsx(Input.TriggerButton, __assign({ size: size }, triggerProps, { onClick: state.toggle, ref: triggerRef, "aria-label": "Time picker" }, { children: jsx(ClockIcon, {}) })) })), jsx(Popover, __assign({ triggerRef: triggerRef, isOpen: state.isOpen, onClose: state.close, placement: "bottom end", className: styles.popover, hasFocusTrap: true, hasAutoFocus: true }, overlayProps, { children: jsx(TimePicker, { hour: formattedValue.hour, minute: formattedValue.minute, isAM: formattedValue.isAM, onPickTime: onPickTime }) }))] }));
};

export { TimeInput };
